import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { PMButton, PMSegmented, PMBadge } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { useLocations, useVisits } from "@/data/queries";
import { levelDef } from "@/lib/levels";
import { LocationMap } from "@/ui/LocationMap";
import { VisitStatusBadge } from "@/ui/visit";
import { recency } from "@/features/locations/LocationsPage";
import { relTime } from "@/ui/format";
import "./map.css";

// The map is the one full-bleed surface (README §3). Floating panels are glass.
export function MapPage() {
  const { teamId } = useParams({ from: "/t/$teamId/map" });
  const nav = useNavigate();
  const { hierarchy } = useApp();
  const search = useSearch({ from: "/t/$teamId/map" });
  const level = search.level || hierarchy.levels.at(-1)!.key;
  const setLevel = (l: string) => nav({ to: ".", search: { level: l }, replace: true } as any);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const locs = useLocations(level);
  const visits = useVisits({ limit: 400 });
  const def = levelDef(level, hierarchy.key);

  const rows = useMemo(() => locs.data.filter((l) => !q || (l.label || "").toLowerCase().includes(q.toLowerCase())), [locs.data, q]);
  const selected = rows.find((r) => r.id === sel) || locs.data.find((r) => r.id === sel);
  const latest = visits.data.find((v) => v.map_feature_id === sel);

  return (
    <div className="mapview">
      <LocationMap height="100%" selectedId={sel} onSelect={setSel}
        items={rows.map((r) => { const rc = recency(r.last_visit); return { id: r.id, label: r.label || "", geometry: r.geometry, latitude: r.latitude, longitude: r.longitude, tone: rc.tone, sub: r.last_visit ? `Visited ${rc.label}` : "Never visited" }; })} />
      <aside className="mapview-panel pm-glass">
        {hierarchy.levels.length > 1 && <PMSegmented value={level} onChange={(v: string) => { setLevel(v); setSel(null); }} options={hierarchy.levels.map((l) => ({ value: l.key, label: l.plural }))} />}
        <input className="mapview-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${def.plural.toLowerCase()}`} aria-label={`Search ${def.plural}`} />
        <div className="mapview-count hint">{rows.length} {rows.length === 1 ? def.label.toLowerCase() : def.plural.toLowerCase()}</div>
        <div className="mapview-list">
          {rows.map((r) => {
            const rc = recency(r.last_visit);
            return (
              <button key={r.id} className={`mapview-row ${sel === r.id ? "is-sel" : ""}`} onClick={() => setSel(r.id)}>
                <span>{def.emoji}</span>
                <span className="list-main"><span className="list-title">{r.label}</span><span className="meta">{r.parent_label}{r.parent_label && <span className="sep" />}{r.last_visit ? rc.label : "Never visited"}</span></span>
                <i className={`rdot rdot--${rc.tone}`} />
              </button>
            );
          })}
        </div>
      </aside>
      {selected && (
        <div className="mapview-card pm-glass">
          <div className="row"><span className="emoji-disc">{def.emoji}</span><div className="list-main"><div className="list-title">{selected.label}</div><div className="meta">{selected.parent_label}{selected.parent_label && <span className="sep" />}{selected.visit_count} visits</div></div><button className="iconbtn" aria-label="Close" onClick={() => setSel(null)}>✕</button></div>
          {latest ? (
            <button className="mapview-latest" onClick={() => nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: latest.id } })}>
              <div className="row"><strong>{latest.title}</strong><span className="spacer" /><VisitStatusBadge v={latest} /></div>
              <p className="clamp-2 muted">{latest.ai_summary || "No summary yet."}</p>
              <span className="hint">{relTime(latest.started_at)}</span>
            </button>
          ) : <PMBadge variant="neutral">No visits yet</PMBadge>}
          <PMButton block onClick={() => nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: selected.id } })}>Open {def.label.toLowerCase()}</PMButton>
        </div>
      )}
    </div>
  );
}
